// Generates suggestions for which creatures to put in an encounter.
//
// Difficulty uses the same math as the difficulty bar in EncounterParent:
// each team (participant color) has XP thresholds summed from its members,
// and its difficulty is the total XP of every other team compared to those thresholds.
// Allies share the party's color, so they raise the party's thresholds just like the bar does.
//
// Everything here works on plain data, so it can be tested without the api.

export const ENCOUNTER_DIFFICULTIES = ["Easy", "Medium", "Hard", "Deadly"] as const;
export type EncounterDifficulty = typeof ENCOUNTER_DIFFICULTIES[number];

export const ENCOUNTER_TYPES = [
  "Boss with Minions",
  "Boss Alone",
  "2 Groups",
  "2 Groups That Oppose Each Other",
  "3 Groups",
  "3 Groups That Oppose Each Other",
  "Allied Group vs 1 Group",
  "Allied Group vs 2 Groups",
  "Random",
] as const;
export type EncounterType = typeof ENCOUNTER_TYPES[number];

export interface Thresholds { easy: number, medium: number, hard: number, deadly: number }

export interface GenCreature {
  _id: string;
  name: string;
  creature_type: string;
  challenge_rating: number;
  min_challenge_rating: number;
  max_challenge_rating: number;
  environments: string[];
}

export interface SuggestionMember {
  creature_id: string;
  name: string;
  creature_type: string;
  cr: number;
  count: number;
  xp_each: number;
}

export interface SuggestionGroup {
  label: string;
  color: string;
  ally: boolean;
  members: SuggestionMember[];
  xp: number;
}

export interface EncounterSuggestion {
  encounter_type: Exclude<EncounterType, "Random">;
  groups: SuggestionGroup[];
  enemy_xp: number; // XP of all the new enemy groups together
}

export interface GenerateOptions {
  creatures: GenCreature[];
  known_environment_ids: string[]; // All Environments that exist, so leftover values on creatures can be ignored
  environment_ids: string[]; // The encounter's Environments
  creature_types: string[]; // Each of these must show up somewhere in the enemy groups
  difficulty: EncounterDifficulty;
  encounter_type: EncounterType;
  party_color: string;
  party_thresholds: Thresholds; // From the difficulty bar, for the party's color
  existing_enemy_xp: number; // XP already opposing the party in the encounter
  cr_to_xp: { [cr: number]: number };
  level_thresholds: { [level: number]: Thresholds };
  count?: number;
  rng?: () => number;
}

export interface GenerateResult {
  suggestions: EncounterSuggestion[];
  error?: string;
}

// Matches ColorButtonBox
const TEAM_COLORS = ["red", "blue", "green", "purple", "orange", "yellow", "black", "gray", "white"];
const ATTEMPTS_PER_SUGGESTION = 300;

interface Entry {
  creature: GenCreature;
  cr: number;
  xp: number;
}

interface TypePlan {
  enemy_groups: number;
  opposing: boolean;
  boss: "with_minions" | "alone" | "";
  ally: boolean;
}

const PLANS: { [type in Exclude<EncounterType, "Random">]: TypePlan } = {
  "Boss with Minions": { enemy_groups: 1, opposing: false, boss: "with_minions", ally: false },
  "Boss Alone": { enemy_groups: 1, opposing: false, boss: "alone", ally: false },
  "2 Groups": { enemy_groups: 2, opposing: false, boss: "", ally: false },
  "2 Groups That Oppose Each Other": { enemy_groups: 2, opposing: true, boss: "", ally: false },
  "3 Groups": { enemy_groups: 3, opposing: false, boss: "", ally: false },
  "3 Groups That Oppose Each Other": { enemy_groups: 3, opposing: true, boss: "", ally: false },
  "Allied Group vs 1 Group": { enemy_groups: 1, opposing: false, boss: "", ally: true },
  "Allied Group vs 2 Groups": { enemy_groups: 2, opposing: false, boss: "", ally: true },
};
const CONCRETE_TYPES = Object.keys(PLANS) as Exclude<EncounterType, "Random">[];

// Groups that fight each other wear each other down, so together they get a bigger budget.
export const opposingBoost = (groups: number) => 1 + 0.5 * (groups - 1);

// How many different creature types an encounter type can hold, which caps how many can be required.
export const maxCreatureTypes = (encounter_type: EncounterType) => {
  if (encounter_type === "Boss Alone") {
    return 1;
  }
  return 99;
};

// Same clamping the difficulty bar uses for creatures and characters.
export const thresholdsForLevel = (level: number, level_thresholds: { [level: number]: Thresholds }): Thresholds => {
  return level < 1 ? level_thresholds[1] : (level > 20 ? level_thresholds[20] : level_thresholds[Math.floor(level)]);
};

// The XP range the opposing teams need to land in for the bar to show the difficulty.
export const difficultyRange = (thresholds: Thresholds, difficulty: EncounterDifficulty) => {
  switch (difficulty) {
    case "Easy": return { lo: thresholds.easy, hi: thresholds.medium };
    case "Medium": return { lo: thresholds.medium, hi: thresholds.hard };
    case "Hard": return { lo: thresholds.hard, hi: thresholds.deadly };
    case "Deadly": return { lo: thresholds.deadly, hi: thresholds.deadly * 1.5 };
  }
};

// The label the difficulty bar shows for a team, given the XP opposing it.
export const difficultyFor = (thresholds: Thresholds, opposing_xp: number): EncounterDifficulty | "Super Easy" => {
  if (opposing_xp > thresholds.deadly) {
    return "Deadly";
  } else if (opposing_xp > thresholds.hard) {
    return "Hard";
  } else if (opposing_xp > thresholds.medium) {
    return "Medium";
  } else if (opposing_xp > thresholds.easy) {
    return "Easy";
  }
  return "Super Easy";
};

export const fitsEnvironments = (creature: GenCreature, environment_ids: string[], known_environment_ids: string[]) => {
  if (environment_ids.length === 0) {
    return true;
  }
  const known = creature.environments.filter(o => known_environment_ids.includes(o));
  // Creatures without any Environments fit anywhere
  return known.length === 0 || known.some(o => environment_ids.includes(o));
};

// Every CR a creature can be used at, so scalable creatures can fill more budgets.
export const crOptions = (creature: GenCreature, cr_to_xp: { [cr: number]: number }): number[] => {
  const min = creature.min_challenge_rating;
  const max = creature.max_challenge_rating;
  if (max > min && max > 0) {
    return Object.keys(cr_to_xp).map(o => +o).filter(o => o >= min && o <= max);
  }
  return [creature.challenge_rating];
};

const buildEntries = (creatures: GenCreature[], cr_to_xp: { [cr: number]: number }): Entry[] => {
  const entries: Entry[] = [];
  for (const creature of creatures) {
    for (const cr of crOptions(creature, cr_to_xp)) {
      const xp = cr_to_xp[cr];
      if (xp !== undefined) {
        entries.push({ creature, cr, xp });
      }
    }
  }
  return entries;
};

const randBetween = (rng: () => number, min: number, max: number) => min + rng() * (max - min);
const randInt = (rng: () => number, min: number, max: number) => Math.floor(randBetween(rng, min, max + 1));
const pick = <T>(rng: () => number, arr: T[]): T => arr[Math.floor(rng() * arr.length)];
const shuffled = <T>(rng: () => number, arr: T[]): T[] => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; --i) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// Picks one kind of creature and how many of it to fill a budget.
const pickMember = (
  rng: () => number,
  entries: Entry[],
  budget: number,
  { min_count, max_count, creature_type, max_xp, used_ids }:
    { min_count: number, max_count: number, creature_type?: string, max_xp?: number, used_ids: Set<string> }
): SuggestionMember | null => {
  const counts = shuffled(rng, Array.from({ length: max_count - min_count + 1 }, (_, i) => min_count + i));
  for (const count of counts) {
    const per = budget / count;
    const fits = (o: Entry) =>
      o.xp <= per && o.xp >= per * 0.6 &&
      (creature_type === undefined || o.creature.creature_type === creature_type) &&
      (max_xp === undefined || o.xp < max_xp);
    let candidates = entries.filter(o => fits(o) && !used_ids.has(o.creature._id));
    if (candidates.length === 0) {
      // Repeating a creature across groups is better than nothing
      candidates = entries.filter(fits);
    }
    if (candidates.length > 0) {
      const entry = pick(rng, candidates);
      used_ids.add(entry.creature._id);
      return {
        creature_id: entry.creature._id,
        name: entry.creature.name,
        creature_type: entry.creature.creature_type,
        cr: entry.cr,
        count,
        xp_each: entry.xp,
      };
    }
  }
  return null;
};

// Splits a budget into random shares that add up to it.
const splitBudget = (rng: () => number, budget: number, parts: number): number[] => {
  const weights = Array.from({ length: parts }, () => randBetween(rng, 0.5, 1.5));
  const total = weights.reduce((a, b) => a + b, 0);
  return weights.map(o => budget * o / total);
};

const buildGroup = (
  rng: () => number,
  entries: Entry[],
  budget: number,
  boss: TypePlan["boss"],
  required_types: string[],
  used_ids: Set<string>,
  max_count: number
): SuggestionMember[] | null => {
  const members: SuggestionMember[] = [];
  if (boss === "alone") {
    const boss_member = pickMember(rng, entries, budget, { min_count: 1, max_count: 1, creature_type: required_types[0], used_ids });
    return boss_member ? [boss_member] : null;
  }
  if (boss === "with_minions") {
    const boss_member = pickMember(rng, entries, budget * randBetween(rng, 0.5, 0.7), { min_count: 1, max_count: 1, creature_type: required_types[0], used_ids });
    if (!boss_member) {
      return null;
    }
    members.push(boss_member);
    const minion_types = required_types.slice(1);
    const kinds = Math.max(minion_types.length, randInt(rng, 1, 2));
    const shares = splitBudget(rng, budget - boss_member.xp_each, kinds);
    for (let i = 0; i < kinds; ++i) {
      const minion = pickMember(rng, entries, shares[i], { min_count: 2, max_count: 8, creature_type: minion_types[i], max_xp: boss_member.xp_each, used_ids });
      if (!minion) {
        return null;
      }
      members.push(minion);
    }
    return members;
  }
  const kinds = Math.max(required_types.length, randInt(rng, 1, 3));
  const shares = splitBudget(rng, budget, kinds);
  for (let i = 0; i < kinds; ++i) {
    const member = pickMember(rng, entries, shares[i], { min_count: 1, max_count, creature_type: required_types[i], used_ids });
    if (!member) {
      return null;
    }
    members.push(member);
  }
  return members;
};

const groupXP = (members: SuggestionMember[]) => members.reduce((total, o) => total + o.xp_each * o.count, 0);

const signature = (suggestion: EncounterSuggestion) => suggestion.groups.map(g =>
  g.members.map(m => `${m.creature_id}@${m.cr}x${m.count}`).sort().join(",")).join("|");

const buildSuggestion = (
  rng: () => number,
  entries: Entry[],
  encounter_type: Exclude<EncounterType, "Random">,
  options: GenerateOptions
): EncounterSuggestion | null => {
  const plan = PLANS[encounter_type];
  const used_ids = new Set<string>();
  const hostile_colors = TEAM_COLORS.filter(o => o !== options.party_color);
  const groups: SuggestionGroup[] = [];

  let thresholds = { ...options.party_thresholds };
  if (plan.ally) {
    const ally_budget = difficultyRange(options.party_thresholds, options.difficulty).lo / 3;
    const members = buildGroup(rng, entries, randBetween(rng, 0.6, 1) * ally_budget, "", [], used_ids, 4);
    if (!members) {
      return null;
    }
    // Allies count toward the party's thresholds, the same way the difficulty bar counts them
    for (const member of members) {
      const t = thresholdsForLevel(member.cr, options.level_thresholds);
      thresholds = {
        easy: thresholds.easy + t.easy * member.count,
        medium: thresholds.medium + t.medium * member.count,
        hard: thresholds.hard + t.hard * member.count,
        deadly: thresholds.deadly + t.deadly * member.count,
      };
    }
    groups.push({ label: "Allies", color: options.party_color, ally: true, members, xp: groupXP(members) });
  }

  const range = difficultyRange(thresholds, options.difficulty);
  const boost = plan.opposing ? opposingBoost(plan.enemy_groups) : 1;
  const lo = (range.lo - options.existing_enemy_xp) * boost;
  const hi = (range.hi - options.existing_enemy_xp) * boost;
  if (hi <= 0) {
    return null;
  }
  // Aim inside the range, leaving room for picks to come in under their budgets
  const target = randBetween(rng, Math.max(lo, 0) + (hi - Math.max(lo, 0)) * 0.4, hi);
  const group_budgets = splitBudget(rng, target, plan.enemy_groups);

  for (let g = 0; g < plan.enemy_groups; ++g) {
    // Required types are dealt out to the enemy groups
    const required_types = options.creature_types.filter((_, i) => i % plan.enemy_groups === g);
    const members = buildGroup(rng, entries, group_budgets[g], plan.boss, shuffled(rng, required_types), used_ids, 6);
    if (!members) {
      return null;
    }
    const label = plan.boss === "with_minions" ? "Boss & Minions" :
      (plan.boss === "alone" ? "Boss" : (plan.enemy_groups > 1 ? `Group ${g + 1}` : "Enemies"));
    groups.push({
      label,
      color: plan.opposing ? hostile_colors[g] : hostile_colors[0],
      ally: false,
      members,
      xp: groupXP(members),
    });
  }

  const enemy_xp = groups.filter(o => !o.ally).reduce((total, o) => total + o.xp, 0);
  if (enemy_xp <= lo || enemy_xp > hi) {
    return null;
  }
  return { encounter_type, groups, enemy_xp };
};

export const generateEncounterSuggestions = (options: GenerateOptions): GenerateResult => {
  const rng = options.rng ?? Math.random;
  const count = options.count ?? 5;
  if (options.creature_types.length > maxCreatureTypes(options.encounter_type)) {
    return { suggestions: [], error: `${options.encounter_type} can only include ${maxCreatureTypes(options.encounter_type)} creature type.` };
  }
  if (options.party_thresholds.deadly <= 0) {
    return { suggestions: [], error: "Add the party to the encounter first so there's something to measure difficulty against." };
  }
  const pool = options.creatures.filter(o => fitsEnvironments(o, options.environment_ids, options.known_environment_ids));
  const missing_types = options.creature_types.filter(t => !pool.some(o => o.creature_type === t));
  if (missing_types.length > 0) {
    return { suggestions: [], error: `No creatures fit the Environments with type: ${missing_types.join(", ")}.` };
  }
  const entries = buildEntries(pool, options.cr_to_xp);

  const suggestions: EncounterSuggestion[] = [];
  const seen = new Set<string>();
  for (let attempt = 0; attempt < ATTEMPTS_PER_SUGGESTION * count && suggestions.length < count; ++attempt) {
    const encounter_type = options.encounter_type === "Random" ? pick(rng, CONCRETE_TYPES) : options.encounter_type;
    if (options.creature_types.length > maxCreatureTypes(encounter_type)) {
      continue;
    }
    const suggestion = buildSuggestion(rng, entries, encounter_type, options);
    if (suggestion) {
      const sig = signature(suggestion);
      if (!seen.has(sig)) {
        seen.add(sig);
        suggestions.push(suggestion);
      }
    }
  }
  if (suggestions.length === 0) {
    return { suggestions, error: "Couldn't find any combinations that fit. Try fewer creature types, more Environments, or a different difficulty." };
  }
  return { suggestions };
};
